import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useAdmin } from '@/hooks/useAdmin';
import {
  BookOpen, Home, BarChart2, Zap,
  Settings, BookIcon, PenTool, FileText,
  Award, Bot, Video, Users, ChevronLeft, ChevronRight,
  Code, Shield, History, Sparkles, MessageSquare, BadgeDollarSign
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuthSession } from '@/hooks/useAuthSession';
import { supabase } from '@/lib/supabase';
import { hasFeature } from '@/utils/featureAccess';

interface SidebarProps {
  isMobile?: boolean;
  onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isMobile = false, onMobileClose }) => {
  const [collapsed, setCollapsed] = useState(!isMobile);
  const { isAdmin } = useAdmin();
  const { session } = useAuthSession();
  const [isApproved, setIsApproved] = useState<boolean>(false);

  useEffect(() => {
    const checkUserAccess = async () => {
      if (!session?.user) return;
      if (session.user.email === 'admin@admin.com') {
        setIsApproved(true);
        return;
      }
      try {
        const { data, error } = await supabase.functions.invoke('check-auth-permissions');
        if (!error) {
          setIsApproved(data.is_admin || data.is_allowed_access);
        }
      } catch (err) {
        console.error("Access check failed", err);
      }
    };
    checkUserAccess();
  }, [session]);

  const menuItems = [
    { title: 'Dashboard', path: '/dashboard', icon: Home },
    { title: 'Adaptive Quiz Assessment', path: '/quiz', icon: BookOpen, feature: 'Adaptive Quiz Assessment' },
    { title: 'Quiz History', path: '/quiz/history', icon: History, feature: 'Adaptive Quiz Assessment' },
    { title: 'Advanced Analytics', path: '/analytics', icon: BarChart2, feature: 'Advanced Analytics' },
    { title: 'Adaptive Flashcards', path: '/flashcards', icon: Zap, feature: 'Adaptive Flash Cards' },
    { title: 'Adaptive Learning Path', path: '/learning-path', icon: Award, feature: 'Adaptive Learning Path' },
    { title: 'Adaptive Study Notes', path: '/study-notes', icon: FileText, feature: 'Adaptive Study Notes' },
    { title: 'Adaptive Current Affairs', path: '/current-affairs', icon: BookIcon, feature: 'Adaptive Current Affairs' },
    { title: 'Adaptive Mock Tests', path: '/mock-test-generator', icon: PenTool, feature: 'Adaptive Mock Tests' },
    { title: 'PYQ Multiplier', path: '/pyq-multiplier', icon: Sparkles, feature: 'PYQ Multiplier' },
    { title: 'AI Learning Studio', path: '/ai-tutoring', icon: Bot, feature: 'AI Learning Studio' },
    { title: 'Adaptive Video Learning', path: '/video-learning', icon: Video, feature: 'Adaptive Video Recommendation' },
    { title: 'Collaborative Learning', path: '/community', icon: Users, feature: 'Collaborative Learning' },
    { title: 'Code Playground', path: '/playground', icon: Code, feature: 'Code Playground' },
    { title: 'Affiliate Program', path: '/affiliate/dashboard', icon: BadgeDollarSign },
    { title: 'Submit Feedback', path: '/feedback', icon: MessageSquare }
  ];

  const toggleSidebar = () => {
    if (!isMobile) setCollapsed(!collapsed);
  };

  const renderMenuItem = (item: any) => {
    const visible = !item.feature || isAdmin || isApproved || hasFeature(item.feature);
    if (!visible) return null;
    return (
      <NavLink
        key={item.path}
        to={item.path}
        onClick={onMobileClose}
        className={({ isActive }) =>
          cn(
            'group flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-all duration-200',
            isActive
              ? 'bg-primary text-white shadow-sm'
              : 'text-gray-700 hover:bg-gray-100 hover:text-black',
            collapsed ? 'justify-center px-2' : 'pl-4'
          )
        }
        title={collapsed ? item.title : undefined}
      >
        <item.icon className={cn('h-5 w-5 transition-transform group-hover:scale-105')} />
        {!collapsed && <span className="transition-opacity">{item.title}</span>}
      </NavLink>
    );
  };

  return (
    <div
      className={cn(
        'h-screen border-r bg-white transition-all duration-300 ease-in-out relative overflow-y-auto',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      <div className="p-3 flex flex-col h-full">
        {!isMobile && (
          <div className="flex justify-end mb-2">
            <Button variant="ghost" size="icon" onClick={toggleSidebar} className="rounded-full">
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          </div>
        )}

        <div className="space-y-1 flex-1">
          {menuItems.map(renderMenuItem)}
        </div>

        <div className="space-y-1 pt-2">
          {isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                cn(
                  'flex items-center rounded-md px-3 py-2 text-sm transition-all',
                  isActive
                    ? 'bg-destructive text-white'
                    : 'text-gray-500 hover:bg-gray-100 hover:text-black',
                  collapsed && 'justify-center px-2'
                )
              }
              title={collapsed ? 'Admin' : undefined}
            >
              <Shield className="h-5 w-5" />
              {!collapsed && <span>Admin</span>}
            </NavLink>
          )}
          <NavLink
            to="/settings"
            className={({ isActive }) =>
              cn(
                'flex items-center rounded-md px-3 py-2 text-sm transition-all',
                isActive
                  ? 'bg-primary text-white'
                  : 'text-gray-600 hover:bg-gray-100 hover:text-black',
                collapsed && 'justify-center px-2'
              )
            }
            title={collapsed ? 'Settings' : undefined}
          >
            <Settings className="h-5 w-5" />
            {!collapsed && <span>Settings</span>}
          </NavLink>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;